import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { BarChart3, Download, Calendar, Users, Clock, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { shiftApi, employeeApi, departmentApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const COLORS = ['#1E3A5F', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const ShiftReports: React.FC = () => {
  const { toast } = useToast();
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const { data: shifts = [] } = useQuery({ queryKey: ['shifts'], queryFn: () => shiftApi.shifts.getAll() });
  const { data: assignments = [] } = useQuery({ queryKey: ['shiftAssignments'], queryFn: () => shiftApi.assignments.getAll({}) });
  const { data: employees = [] } = useQuery({ queryKey: ['employees'], queryFn: () => employeeApi.getAll({}) });
  const { data: departments = [] } = useQuery({ queryKey: ['departments'], queryFn: () => departmentApi.getAll() });

  // Filter data based on selected department
  const filteredEmployees = useMemo(() => {
    if (selectedDepartment === 'all') return employees;
    return employees.filter((emp: any) => emp.department_id === selectedDepartment);
  }, [employees, selectedDepartment]);

  const filteredAssignments = useMemo(() => {
    if (selectedDepartment === 'all') return assignments;
    const deptEmployeeIds = filteredEmployees.map((emp: any) => emp.id);
    return assignments.filter((a: any) => deptEmployeeIds.includes(a.employee_id));
  }, [assignments, filteredEmployees, selectedDepartment]);

  const totalHours = filteredAssignments.reduce((sum: number, a: any) => sum + (a.hours || 8), 0);
  const avgHoursPerEmployee = filteredEmployees.length > 0 ? (totalHours / filteredEmployees.length).toFixed(1) : 0;
  const coverageRate = filteredAssignments.length > 0 ? Math.round((filteredAssignments.filter((a: any) => a.status === 'CONFIRMED').length / filteredAssignments.length) * 100) : 0;

  // Prepare chart data - Shift Distribution by Type
  const shiftDistributionData = useMemo(() => {
    const distribution: { [key: string]: number } = {};
    filteredAssignments.forEach((assignment: any) => {
      const shift = shifts.find((s: any) => s.id === assignment.shift_id);
      if (shift) {
        distribution[shift.name] = (distribution[shift.name] || 0) + 1;
      }
    });
    return Object.entries(distribution).map(([name, count]) => ({ name, count }));
  }, [shifts, filteredAssignments]);

  // Prepare coverage trends data (mock weekly data for now)
  const coverageTrendsData = useMemo(() => {
    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return daysOfWeek.map((day) => {
      const dayAssignments = filteredAssignments.filter((a: any) => {
        const date = new Date(a.date);
        return daysOfWeek[date.getDay() === 0 ? 6 : date.getDay() - 1] === day;
      });
      const confirmedCount = dayAssignments.filter((a: any) => a.status === 'CONFIRMED').length;
      const coveragePercent = dayAssignments.length > 0 ? Math.round((confirmedCount / dayAssignments.length) * 100) : 0;
      return {
        day,
        coverage: coveragePercent,
        total: dayAssignments.length,
        confirmed: confirmedCount,
      };
    });
  }, [filteredAssignments]);

  // Employee hours summary
  const employeeHoursSummary = useMemo(() => {
    const summary: { [key: string]: { name: string; hours: number; shifts: number; department: string } } = {};
    
    filteredAssignments.forEach((assignment: any) => {
      const employee = employees.find((e: any) => e.id === assignment.employee_id);
      if (employee) {
        if (!summary[employee.id]) {
          const dept = departments.find((d: any) => d.id === employee.department_id);
          summary[employee.id] = {
            name: `${employee.first_name} ${employee.last_name}`,
            hours: 0,
            shifts: 0,
            department: dept?.name || 'N/A',
          };
        }
        summary[employee.id].hours += assignment.hours || 8;
        summary[employee.id].shifts += 1;
      }
    });

    return Object.values(summary).sort((a, b) => b.hours - a.hours);
  }, [filteredAssignments, employees, departments]);

  // Export report to CSV
  const handleExportReport = () => {
    const csvRows = [];
    csvRows.push(['Report Type', 'Shift Reports & Analytics'].join(','));
    csvRows.push(['Period', selectedPeriod].join(','));
    csvRows.push(['Generated At', new Date().toISOString()].join(','));
    csvRows.push([]);
    
    // Summary stats
    csvRows.push(['--- Summary Statistics ---'].join(','));
    csvRows.push(['Metric', 'Value'].join(','));
    csvRows.push(['Total Hours', totalHours].join(','));
    csvRows.push(['Avg Hours/Employee', avgHoursPerEmployee].join(','));
    csvRows.push(['Coverage Rate', `${coverageRate}%`].join(','));
    csvRows.push(['Active Shifts', shifts.filter((s: any) => s.is_active).length].join(','));
    csvRows.push([]);
    
    // Shift details
    csvRows.push(['--- Shift Templates ---'].join(','));
    csvRows.push(['Shift Name', 'Start Time', 'End Time', 'Duration (hrs)', 'Status'].join(','));
    shifts.forEach((shift: any) => {
      csvRows.push([
        `"${shift.name || ''}"`,
        shift.start_time || '',
        shift.end_time || '',
        shift.duration_hours || 8,
        shift.is_active ? 'Active' : 'Inactive'
      ].join(','));
    });
    csvRows.push([]);
    
    // Employee assignments
    csvRows.push(['--- Employee Assignments ---'].join(','));
    csvRows.push(['Employee ID', 'Shift ID', 'Date', 'Hours', 'Status'].join(','));
    assignments.forEach((assignment: any) => {
      csvRows.push([
        assignment.employee_id || '',
        assignment.shift_id || '',
        assignment.date || '',
        assignment.hours || 8,
        assignment.status || ''
      ].join(','));
    });
    
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `shift-report-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast({ title: 'Report exported successfully' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">View shift statistics and generate reports</h1>
        
        </div>
        <Button onClick={handleExportReport} className="m-2">
          <Download className="h-4 w-4 mr-1.5" />
          <span>Export Report</span>
        </Button>
      </div>

      <div className="flex items-center space-x-4">
        <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
          </SelectContent>
        </Select>
        <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map((dept: any) => (
              <SelectItem key={dept.id} value={dept.id}>
                {dept.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Hours</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalHours}</div>
            <p className="text-xs text-muted-foreground mt-1">Across all employees</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Hours/Employee</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgHoursPerEmployee}</div>
            <p className="text-xs text-muted-foreground mt-1">Per employee</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Coverage Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{coverageRate}%</div>
            <p className="text-xs text-muted-foreground mt-1">Confirmed shifts</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Shifts</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{shifts.filter((s: any) => s.is_active).length}</div>
            <p className="text-xs text-muted-foreground mt-1">Shift types</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Shift Distribution</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            {shiftDistributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={shiftDistributionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#1E3A5F" name="Assignments" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-center text-muted-foreground">
                <div>
                  <BarChart3 className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No shift data available</p>
                  <p className="text-sm mt-2">Assign employees to shifts to see distribution</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Coverage Trends</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            {coverageTrendsData.some(d => d.total > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={coverageTrendsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-3 border rounded shadow-lg">
                            <p className="font-semibold">{data.day}</p>
                            <p className="text-sm">Coverage: {data.coverage}%</p>
                            <p className="text-sm">Confirmed: {data.confirmed}/{data.total}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="coverage" stroke="#10B981" strokeWidth={2} name="Coverage %" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-center text-muted-foreground">
                <div>
                  <TrendingUp className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No coverage data available</p>
                  <p className="text-sm mt-2">Coverage trends will appear as shifts are assigned</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Employee Hours Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {employeeHoursSummary.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Employee</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Department</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Hours</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Shifts</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Avg Hours/Shift</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeHoursSummary.map((emp: any, index: number) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">{emp.name}</td>
                      <td className="py-3 px-4">{emp.department}</td>
                      <td className="text-right py-3 px-4 font-medium">{emp.hours}</td>
                      <td className="text-right py-3 px-4">{emp.shifts}</td>
                      <td className="text-right py-3 px-4">{(emp.hours / emp.shifts).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="py-3 px-4" colSpan={2}>Total</td>
                    <td className="text-right py-3 px-4">{employeeHoursSummary.reduce((sum, emp) => sum + emp.hours, 0)}</td>
                    <td className="text-right py-3 px-4">{employeeHoursSummary.reduce((sum, emp) => sum + emp.shifts, 0)}</td>
                    <td className="text-right py-3 px-4">-</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No employee hours data</p>
              <p className="text-sm mt-2">Assign shifts to employees to see detailed hours breakdown</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShiftReports;